import { Icon, InlineIcon } from "@iconify/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { deleteChatSession } from "../../api/deleteChatSession";

interface ChatSessionDeleteModalProps {
  isDarkMode: boolean;
  sessionName: string | null;
  sessionId: string | null;
  onClose: () => void;
  onConfirm: () => void;
}

export function ChatSessionDeleteModal({
  isDarkMode,
  sessionName,
  sessionId,
  onClose,
  onConfirm,
}: ChatSessionDeleteModalProps) {
  const queryClient = useQueryClient();

  const deleteSessionMutation = useMutation({
    mutationFn: (chat_session_uid: string) => deleteChatSession(chat_session_uid),
    onSuccess: () => {
      toast.success("Chat session deleted successfully.");
      // Refresh chat sessions list
      queryClient.invalidateQueries({ queryKey: ["chatSessions"] });
      // Call the onConfirm callback to handle cleanup in parent
      onConfirm();
    },
    onError: (error) => {
      const errorMessage =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Failed to delete chat session. Please try again.";
      toast.error(errorMessage);
    },
  });

  const handleConfirmDelete = () => {
    if (!sessionId) {
      return;
    }
    deleteSessionMutation.mutate(sessionId);
  };
  return (
    <div className="Modal ChatSessionDeleteModal" data-dark-mode={isDarkMode}>
      <div
        className={[
          "Modal__card",
          "ChatSessionDeleteModal__card",
          isDarkMode ? "bg-gray-800" : "bg-white",
        ].join(" ")}
      >
        <div
          className={[
            "Modal__card__header",
            "ChatSessionDeleteModal__card__header",
            "rounded-t",
            isDarkMode ? "bg-gray-700" : "bg-gray-100",
          ].join(" ")}
        >
          <h6 className={isDarkMode ? "text-gray-100" : "text-gray-900"}>Delete Chat Session</h6>
          <InlineIcon
            icon="ei:close"
            className={[
              "Modal__card__header__icon",
              isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-700 hover:text-gray-900",
            ].join(" ")}
            onClick={onClose}
          />
        </div>
        <div
          className={[
            "Modal__card__body",
            "ChatSessionDeleteModal__card__body",
            "rounded-b",
            isDarkMode ? "bg-gray-800" : "bg-white",
          ].join(" ")}
        >
          <div className="grid grid-cols-1 gap-4">
            <p className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
              Are you sure you want to delete this chat session?
              {sessionName && <span className="font-semibold"> "{sessionName}"</span>}
            </p>
            <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"} font-bold`}>
              This action cannot be undone.
            </p>
          </div>
        </div>
        <div
          className={`Modal__card__actions grid grid-flow-col auto-cols-max justify-end gap-2 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={deleteSessionMutation.isLoading}
            className={[
              "px-4",
              "py-2",
              "rounded-lg!",
              "transition-colors",
              "font-medium",
              "disabled:opacity-50",
              "disabled:cursor-not-allowed",
              isDarkMode
                ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                : "bg-gray-200 hover:bg-gray-300 text-gray-700",
            ].join(" ")}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteSessionMutation.isLoading}
            className={[
              "px-4",
              "py-2",
              "rounded-lg!",
              "transition-colors",
              "font-medium",
              "bg-red-500",
              "hover:bg-red-600",
              "text-white!",
              "disabled:opacity-50",
              "disabled:cursor-not-allowed",
            ].join(" ")}
          >
            <span className="grid grid-flow-col gap-2 place-items-center auto-cols-max">
              <Icon icon="mdi:delete" className="w-5 h-5" />
              {deleteSessionMutation.isLoading ? "Deleting..." : "Delete"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
